import { createRouter, createWebHistory } from 'vue-router'
import Config from '#/config'
import i18n from '#/locales'

import store from '#/stores'
import { redirectByRole } from '#/stores/auth'
import {
  AUTH_GET_IP_TOKEN,
  AUTH_GET_SESSION_TOKEN,
  AUTH_LOGOUT,
  AUTH_REFRESH,
  AUTH_VERIFY,
} from '@/libvue/apps/auth/actions'
import AuthRoutes from './authRoutes'
import CatalogRoutes from './catalogRoutes'
import DashboardRoutes from './dashboardRoutes'
import FavoritesRoutes from './favoritesRoutes'
// apps pages
import MarketRoutes from './marketRoutes'
import OnboardingRoutes from './onboardRoutes'

import PlaylistsRoutes from './playlistsRoutes'

import SubscribersRoutes from './subscribersRoutes'
import UserRoutes from './userRoutes'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'homeNoLang',
      redirect: () => ({
        name: 'home',
        params: { locale: i18n.global.locale.value },
      }),
    },
    {
      path: '/:locale(en|fr)',
      children: [
        ...AuthRoutes,
        ...CatalogRoutes,
        ...MarketRoutes,
        ...OnboardingRoutes,
        ...FavoritesRoutes,
        ...PlaylistsRoutes,
        ...SubscribersRoutes,
        ...DashboardRoutes,
        UserRoutes,
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      redirect: () => ({
        name: 'not.found',
        params: { locale: i18n.global.locale.value },
      }),
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    // Prevent scrolling if only the results query parameter changed on the same path
    if (to.path === from.path && to.query.results !== from.query.results) {
      return false
    }

    return new Promise((resolve) => {
      setTimeout(() => {
        const pos = savedPosition || { top: 0 }
        resolve(pos)
      }, 400)
    })
  },
})

// Use this to load data upon refresh or login
router.beforeEach((to, from) => {
  if (!to.meta?.layout) {
    to.meta.layout = 'default'
  }

  // adding app name for gtm purpose
  to.meta.gtmAdditionalEventData = {
    app: {
      name: Config.decodedHost,
    },
  }
  // set locale from route param
  if (to.params.locale) {
    localStorage.setItem('pref-lang', to.params.locale)
    i18n.global.locale.value = to.params.locale
  }
  // set document title from route param
  if (to.meta.title) {
    document.title = i18n.global.t(`${to.meta.title}`)
  }
  else {
    document.title = i18n.global.t('pageMeta.default.title')
  }
  let promise = ''
  // refresh token if it exists in local storage
  if (
    !store.getters.getProfile.user_uid
    && localStorage.getItem('refresh-token')
  ) {
    promise = AUTH_REFRESH
  }
  // try session token
  else if (
    !from.name
    && !store.getters.getProfile.user_uid
    && !localStorage.getItem('refresh-token')
  ) {
    promise = AUTH_GET_SESSION_TOKEN
  }

  return promise
    ? store
        .dispatch(promise)
        .then((res) => {
          const localData = res.data ? res.data : res
          if (
            store.getters.getProfile.agreement_version
            !== Config.agreement.version
          ) {
            store.dispatch(AUTH_LOGOUT)
            return true
          }
          if (
            !localData.auth.team_uid
            && redirectByRole(localData.auth).name !== to.name
          ) {
            const redirect = {
              ...redirectByRole(localData.auth),
              params: { locale: i18n.global.locale.value },
              query: { redirect: to.fullPath },
            }
            return redirect
          }

          return true
        })
        .catch(() => {
          return store
            .dispatch(AUTH_GET_IP_TOKEN)
            .then(() => true)
            .catch(() => {
              if (localStorage.getItem('user-token')) {
                return store
                  .dispatch(AUTH_VERIFY)
                  .then(() => true)
                  .catch(() => true)
              }
              else {
                return true
              }
            })
        })
    : true
})

router.afterEach((to, from) => {
  /*
  Translate current route url by getting the routes meta.alias if it exists.
  */

  if (i18n.global.locale.value === 'fr' && to.meta.alias) {
    const result = []
    const params = to.fullPath.split(to.path).pop()
    const pathSplit = to.path.split('/')

    to.meta.alias.forEach((term, index) => {
      if (term) {
        result.push(term)
      }
      else {
        result.push(pathSplit[index])
      }
    })

    let resultPath = result.join('/')

    if (params) {
      resultPath = resultPath + params
    }

    if (resultPath !== to.fullPath) {
      history.replaceState({}, null, resultPath)
    }
  }
})

export default router
